import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNotificationPreferencesContext } from "@/context/NotificationPreferencesContext";
import { 
  db, 
  requestNotificationPermission, 
  setupMessageListener
} from "@/lib/firebase";
import {
  sendPhoneVerificationCode,
  verifyPhoneCode,
  unlinkPhoneProvider,
  cleanupRecaptcha
} from "@/lib/phoneAuth";
import { 
  doc, 
  collection,
  addDoc,
  query,
  where,
  getDocs,
  Timestamp,
  deleteDoc,
  updateDoc
} from "firebase/firestore";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Bell, Mail, Phone, CheckIcon, ChevronsUpDown, AlertCircle, Loader2, Smartphone, Trash, CalendarDays } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CountryOption, formatAsYouType, getAllCountries, isValidPhoneNumber, formatE164 } from "@/lib/phoneUtils";
import { CountryCode } from "libphonenumber-js";
import { PhoneVerificationDialog } from "@/components/PhoneVerificationDialog";
import { config } from "@/config/environment";
import { ConfirmationResult } from "firebase/auth";
import * as UAParserLib from "ua-parser-js";

const UAParser = UAParserLib.UAParser;

interface FCMToken {
  token: string;
  device: string;
  createdAt: Timestamp;
  lastUsedAt: Timestamp;
  id?: string;
}

const countryCodes: CountryOption[] = getAllCountries();

export function NotificationSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  // Usar el contexto centralizado para las preferencias
  const {
    preferences,
    isLoading,
    isSaving,
    error,
    updateWhatsAppPreferences,
    updateEmailPreferences,
    updateWebPushPreferences,
    updateDailySummaryPreferences,
    refreshPreferences
  } = useNotificationPreferencesContext();

  // Local states for specific UI only
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [phoneNumberValid, setPhoneNumberValid] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  
  const [tempPhoneNumber, setTempPhoneNumber] = useState("");
  const [tempCountryCode, setTempCountryCode] = useState<CountryCode>("US");
  const [tempCountryDialCode, setTempCountryDialCode] = useState("+1");
  const [isVerificationNeeded, setIsVerificationNeeded] = useState(false);
  const [currentDeviceInfo, setCurrentDeviceInfo] = useState<string>("");
  const [currentFcmToken, setCurrentFcmToken] = useState<string | null>(null);

  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [showWhatsAppPrompt, setShowWhatsAppPrompt] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [isVerificationInProgress, setIsVerificationInProgress] = useState(false);
  
  const [registeredDevices, setRegisteredDevices] = useState<FCMToken[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isRemovingDevice, setIsRemovingDevice] = useState<string | null>(null);
  const [isAddingCurrentDevice, setIsAddingCurrentDevice] = useState(false);
  const [currentDeviceFound, setCurrentDeviceFound] = useState<boolean | null>(null);

  const notificationsSupported = typeof window !== 'undefined' && 'Notification' in window;

  // Detect current device information
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const parser = new UAParser(window.navigator.userAgent);
      const result = parser.getResult();
      
      const deviceModel = result.device.model || "";
      let browserName = result.browser.name || "Unknown Browser";
      const osName = result.os.name || "Unknown OS";
      
      if (result.browser.version) {
        browserName += ` ${result.browser.version.split('.')[0]}`;
      }
      
      let deviceString = "";
      
      if (deviceModel && deviceModel !== "") {
        deviceString = `${deviceModel} (${browserName}, ${osName})`;
      } else if (osName.includes("iOS")) {
        deviceString = `iPhone/iPad (${browserName}, ${osName})`;
      } else {
        deviceString = `${browserName} on ${osName}`;
      }
      
      console.log("Device info from UAParser:", result);
      console.log("Current device identified as:", deviceString);
      setCurrentDeviceInfo(deviceString);
    }
  }, []);

  // Inicializar valores temporales desde las preferencias
  useEffect(() => {
    if (preferences.whatsapp.phoneNumber) {
      // Extract only the local number from E164 format
      const foundCountry = countryCodes.find((country) =>
        preferences.whatsapp.phoneNumber!.startsWith(country.value)
      );
      if (foundCountry) {
        setTempPhoneNumber(preferences.whatsapp.phoneNumber.substring(foundCountry.value.length));
      } else {
        // If country not found, use full number as fallback
        setTempPhoneNumber(preferences.whatsapp.phoneNumber);
      }
    }
    if (preferences.whatsapp.countryCodeIso) {
      setTempCountryCode(preferences.whatsapp.countryCodeIso);
    }
    if (preferences.whatsapp.countryCode) {
      setTempCountryDialCode(preferences.whatsapp.countryCode);
    }
  }, [preferences.whatsapp.phoneNumber, preferences.whatsapp.countryCodeIso, preferences.whatsapp.countryCode]);

  // Load registered devices and configure FCM if enabled
  useEffect(() => {
    if (user) {
      fetchRegisteredDevices();
      
      if (preferences.webPush?.enabled) {
        requestNotificationPermission().then(token => {
          if (token) {
            setCurrentFcmToken(token);
            console.log("Setting current FCM token:", token);
          }
        }).catch(error => {
          console.error("Error getting FCM token:", error);
        });
      }
    }
  }, [user, preferences.webPush?.enabled]);

  // Validate phone number
  useEffect(() => {
    if (tempPhoneNumber && tempCountryCode) {
      const isValid = isValidPhoneNumber(tempPhoneNumber, tempCountryCode);
      setPhoneNumberValid(isValid);
    } else {
      setPhoneNumberValid(null);
    }
  }, [tempPhoneNumber, tempCountryCode]);

  // Check if re-verification is needed
  useEffect(() => {
    if (preferences.whatsapp.isVerified) {
      const storedNumber = preferences.whatsapp.phoneNumber;
      const storedCountry = preferences.whatsapp.countryCodeIso;
      
      if (tempPhoneNumber !== storedNumber || tempCountryCode !== storedCountry) {
        setIsVerificationNeeded(true);
      } else {
        setIsVerificationNeeded(false);
      }
    }
  }, [tempPhoneNumber, tempCountryCode, preferences.whatsapp.isVerified, preferences.whatsapp.phoneNumber, preferences.whatsapp.countryCodeIso]);

  // Monitor notification permissions
  useEffect(() => {
    if (notificationsSupported) {
      setPermissionStatus(Notification.permission);
      
      const checkPermissionStatus = () => {
        if ('Notification' in window) {
          setPermissionStatus(Notification.permission);
        }
      };
      
      const intervalId = setInterval(checkPermissionStatus, 2000);
      
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [notificationsSupported]);

  // Cleanup
  useEffect(() => {
    return () => {
      cleanupRecaptcha();
    };
  }, []);

  const fetchRegisteredDevices = async (currentToken?: string) => {
    if (!user) return;
    
    try {
      setIsLoadingDevices(true);
      const tokensCollection = collection(db, `users/${user.uid}/settings/notifications/tokens`);
      const querySnapshot = await getDocs(tokensCollection);
      
      const devices: FCMToken[] = [];
      querySnapshot.forEach(doc => {
        const data = doc.data() as FCMToken;
        devices.push({ ...data, id: doc.id });
      });
      
      devices.sort((a, b) => b.lastUsedAt.toMillis() - a.lastUsedAt.toMillis());
      
      setRegisteredDevices(devices);
      
      if (currentToken) {
        console.log("Setting current FCM token from parameter:", currentToken);
        setCurrentFcmToken(currentToken);
      }
      
      console.log("Fetched registered devices:", devices.length);
      
      let foundCurrentDevice = false;
      if (currentFcmToken) {
        console.log("Looking for current token in devices:", currentFcmToken);
        const foundCurrentDeviceByToken = devices.find(device => device.token === currentFcmToken);
        
        if (foundCurrentDeviceByToken) {
          console.log("Found current device by token:", foundCurrentDeviceByToken);
          foundCurrentDevice = true;
        } else {
          console.log("Current token not found in registered devices");
        }
      } 
      else if (currentDeviceInfo) {
        console.log("Looking for current device by device info:", currentDeviceInfo);
        const foundByDeviceInfo = devices.find(device => 
          device.device.toLowerCase() === currentDeviceInfo.toLowerCase()
        );
        
        if (foundByDeviceInfo) {
          console.log("Found current device by device info:", foundByDeviceInfo);
          setCurrentFcmToken(foundByDeviceInfo.token);
          foundCurrentDevice = true;
        } else {
          console.log("Current device info not found in registered devices");
        }
      }
      
      setCurrentDeviceFound(foundCurrentDevice);
      
    } catch (error) {
      console.error("Error fetching registered devices:", error);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const removeDevice = async (tokenId: string) => {
    if (!user) return;
    
    try {
      setIsRemovingDevice(tokenId);
      
      const deviceToRemove = registeredDevices.find(device => device.id === tokenId);
      const isCurrentDevice = deviceToRemove?.token === currentFcmToken;
      
      await deleteDoc(doc(db, `users/${user.uid}/settings/notifications/tokens/${tokenId}`));
      
      setRegisteredDevices(prevDevices => prevDevices.filter(device => device.id !== tokenId));
      
      toast({
        title: "Device removed",
        description: "This device will no longer receive notifications",
      });
      
      if (isCurrentDevice) {
        await updateWebPushPreferences({ enabled: false });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      console.error("Error removing device:", errorMessage);
      toast({
        title: "Error",
        description: "Failed to remove device",
        variant: "destructive",
      });
    } finally {
      setIsRemovingDevice(null);
    }
  };

  const registerCurrentDevice = async () => {
    if (!user) return;
    
    try {
      setIsAddingCurrentDevice(true);
      
      const token = await registerFCMToken();
      if (!token) {
        toast({
          title: "Registration failed",
          description: "Could not register this device. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      await fetchRegisteredDevices(token);
      
      toast({
        title: "Device registered",
        description: "Your current device is now registered for notifications",
      });
      
      setCurrentDeviceFound(true);
    } catch (error) {
      console.error("Error registering current device:", error);
      toast({
        title: "Registration failed",
        description: "Could not register this device. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAddingCurrentDevice(false);
    }
  };

  const registerFCMToken = async (): Promise<string | null> => {
    if (!user) return null;
    
    try {
      const token = await requestNotificationPermission();
      console.log("FCM token obtained:", token);
      
      if (!token) {
        console.log("No FCM token available");
        toast({
          title: "Notification Error",
          description: "Unable to register for push notifications. Please try again later.",
          variant: "destructive",
        });
        return null;
      }
      
      setCurrentFcmToken(token);
      
      const tokensCollection = collection(db, `users/${user.uid}/settings/notifications/tokens`);
      const q = query(tokensCollection, where("token", "==", token));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        const now = Timestamp.now();
        const newToken: FCMToken = {
          token,
          device: currentDeviceInfo,
          createdAt: now,
          lastUsedAt: now
        };
        
        await addDoc(collection(db, `users/${user.uid}/settings/notifications/tokens`), newToken);
        
        console.log("Token saved to tokens collection:", token);
        
        await subscribeToUserTopic(token);
        
        setPermissionStatus('granted');
        return token;
      } else {
        console.log("Token already registered:", token);
        
        await updateExistingToken(token);
        
        setupMessageListener();
        
        setPermissionStatus('granted');
        return token;
      }
    } catch (error) {
      console.error("Error registering FCM token:", error);
      toast({
        title: "Error",
        description: "Failed to register for push notifications",
        variant: "destructive",
      });
      return null;
    }
  };

  const updateExistingToken = async (token: string) => {
    if (!user) return;
    
    try {
      const now = Timestamp.now();
      
      const tokensCollection = collection(db, `users/${user.uid}/settings/notifications/tokens`);
      const q = query(tokensCollection, where("token", "==", token));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const tokenDoc = querySnapshot.docs[0];
        await updateDoc(tokenDoc.ref, {
          lastUsedAt: now,
          device: currentDeviceInfo
        });
        
        console.log("Token timestamp updated:", token);
      }
    } catch (error) {
      console.error("Error updating token timestamp:", error);
    }
  };

  const subscribeToUserTopic = async (token: string) => {
    if (!user) return;
    
    try {
      console.log(`Subscribing token to topic 'user-${user.uid}'`);
      
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      console.error("Error subscribing to user topic:", error);
    }
  };

  const handleToggleChange = async (channel: 'email' | 'webPush' | 'whatsapp' | 'dailySummary', value: boolean) => {
    if (channel === 'webPush' && value) {
      if (!notificationsSupported) {
        toast({
          title: "Not supported",
          description: "Your browser doesn't support web notifications",
          variant: "destructive",
        });
        return;
      }
      
      const token = await registerFCMToken();
      if (!token) {
        return;
      }
      
      await fetchRegisteredDevices(token);
      await updateWebPushPreferences({ enabled: true });
    } else if (channel === 'webPush' && !value) {
      await updateWebPushPreferences({ enabled: false });
    } else if (channel === 'email') {
      await updateEmailPreferences({ enabled: value });
    } else if (channel === 'whatsapp') {
      await updateWhatsAppPreferences({ enabled: value });
    } else if (channel === 'dailySummary') {
      if (value && !preferences.whatsapp.isVerified) {
        toast({
          title: "Phone verification required",
          description: "Once your phone number is verified, you'll start receiving the daily summary of alerts for your children.",
        });
      }
      await updateDailySummaryPreferences({ whatsappEnabled: value });
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formattedValue = formatAsYouType(rawValue, tempCountryCode);
    
    setTempPhoneNumber(formattedValue);
  };

  const handleCountryCodeChange = (countryOption: CountryOption) => {
    setTempCountryDialCode(countryOption.value);
    setTempCountryCode(countryOption.code);
    
    setOpen(false);
    
    setTimeout(() => {
      if (phoneInputRef.current) {
        phoneInputRef.current.focus();
      }
    }, 100);
  };

  const verifyPhoneNumber = async () => {
    if (!phoneNumberValid) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number before verifying",
        variant: "destructive",
      });
      return;
    }
    
    if (isVerificationInProgress) {
      console.log("Verification already in progress, ignoring request");
      return;
    }
    
    try {
      setIsVerificationInProgress(true);
      setIsVerifying(true);
      setVerificationError(null);
      
      const e164Number = formatE164(tempPhoneNumber, tempCountryCode);
      
      if (!e164Number) {
        throw new Error("Could not format phone number");
      }
      
      console.log("Initiating phone verification for:", e164Number);
      
      if (preferences.whatsapp.isVerified && isVerificationNeeded) {
        console.log("Unlinking previous phone provider before linking new one");
        setIsUnlinking(true);
        try {
          await unlinkPhoneProvider();
        } catch (unlinkError) {
          console.log("No previous phone provider to unlink or unlink failed", unlinkError);
        } finally {
          setIsUnlinking(false);
        }
      }
      
      const result = await sendPhoneVerificationCode(e164Number);
      
      setConfirmationResult(result);
      setShowVerificationDialog(true);
      
      toast({
        title: "Verification code sent",
        description: "Please enter the 6-digit code sent to your phone",
      });
    } catch (error) {
      console.error("Error starting phone verification:", error);
      let errorMessage = error instanceof Error ? error.message : "Failed to send verification code";
      
      if (errorMessage.includes("provider-already-linked")) {
        errorMessage = "This phone number is already linked. Please try a different number.";
      }
      
      setVerificationError(errorMessage);
      toast({
        title: "Verification failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
      setTimeout(() => {
        setIsVerificationInProgress(false);
      }, 2000);
    }
  };

  const handleVerifyCode = async (code: string) => {
    try {
      setVerificationError(null);
      
      if (!confirmationResult) {
        throw new Error("Verification session expired. Please try again.");
      }
      
      const result = await verifyPhoneCode(confirmationResult, code);
      
      // Extract only the local number without country code
      const fullPhoneNumber = result.user.phoneNumber || "";
      const phoneNumber = fullPhoneNumber.startsWith(tempCountryDialCode) 
        ? fullPhoneNumber.slice(tempCountryDialCode.length)
        : fullPhoneNumber.replace(/^\+\d+/, ""); // Fallback to remove any country code

      setTempPhoneNumber(phoneNumber);
      
      // Actualizar las preferencias usando el contexto centralizado
      await updateWhatsAppPreferences({
        phoneNumber: phoneNumber,
        countryCode: tempCountryDialCode,
        countryCodeIso: tempCountryCode,
        isVerified: true,
        enabled: true
      });
      
      setIsVerificationNeeded(false);
      
      setShowVerificationDialog(false);
      setShowWhatsAppPrompt(true);
      
      setConfirmationResult(null);
      
      toast({
        title: "Phone number verified",
        description: "Your phone number has been successfully verified",
      });
    } catch (error) {
      console.error("Error verifying code:", error);
      setVerificationError(error instanceof Error ? error.message : "Invalid verification code");
      
      toast({
        title: "Verification failed",
        description: error instanceof Error ? error.message : "Invalid verification code. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getWhatsAppLink = () => {
    const supportNumber = config.whatsapp.supportNumber;
    const message = encodeURIComponent("Hi, I'd like to initiate WhatsApp notifications for my account.");
    return `https://wa.me/${supportNumber}?text=${message}`;
  };

  const requestWebNotificationPermission = async (): Promise<NotificationPermission> => {
    if (!notificationsSupported) return 'denied';
    
    try {
      console.log("Requesting notification permission...");
      const permission = await Notification.requestPermission();
      console.log("Permission result:", permission);
      setPermissionStatus(permission);
      
      if (permission === 'granted') {
        try {
          new Notification("Notifications enabled", {
            body: "You will now receive notifications from our app",
            icon: "/favicon.png"
          });
        } catch (e) {
          console.error("Error creating test notification:", e);
        }
      } else if (permission === 'denied') {
        toast({
          title: "Permission denied",
          description: "You've blocked notifications in your browser settings",
          variant: "destructive",
        });
      }
      
      return permission;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      toast({
        title: "Error",
        description: "Failed to request notification permission",
        variant: "destructive",
      });
      return 'denied';
    }
  };

  const verifyButtonText = () => {
    if (isVerifying) {
      return "Sending Code...";
    }
    if (isUnlinking) {
      return "Unlinking previous number...";
    }
    return "Verify Phone Number";
  };

  const formatTimestamp = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };

  const isCurrentDevice = (device: FCMToken): boolean => {
    return device.token === currentFcmToken;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/3"></div>
        <div className="space-y-4">
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const currentCountry = countryCodes.find(
    code => code.code === tempCountryCode
  ) || countryCodes.find(code => code.value === tempCountryDialCode);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Notification Preferences</h3>
        <p className="text-sm text-muted-foreground">
          Configure how you want to receive notifications from our system.
        </p>
      </div>
      
      <Separator />
      
      <div className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-primary" />
              <Label htmlFor="whatsapp" className="font-medium">
                WhatsApp Notifications
              </Label>
            </div>
            <Switch
              id="whatsapp"
              checked={preferences.whatsapp.enabled}
              disabled={isSaving || isVerifying || isUnlinking}
              onCheckedChange={(value) => handleToggleChange('whatsapp', value)}
            />
          </div>
          
          {preferences.whatsapp.enabled && (
            <div className="pl-6 mt-2 space-y-4">
              <div>
                <Label htmlFor="phone-number" className="text-sm mb-1 block">
                  Phone Number
                </Label>
                <div className="flex gap-2 max-w-sm">
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        role="combobox" 
                        className="w-[120px] justify-between"
                      >
                        {currentCountry ? 
                          currentCountry.flag + " " + currentCountry.value : 
                          "Select"}
                        <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0 max-h-[400px]">
                      <Command>
                        <CommandInput placeholder="Search country code..." />
                        <CommandList className="max-h-[350px]">
                          <CommandEmpty>No country found.</CommandEmpty>
                          <CommandGroup>
                            {countryCodes.map((code) => (
                              <CommandItem
                                key={code.code}
                                value={`${code.code} ${code.label}`}
                                onSelect={() => handleCountryCodeChange(code)}
                                className="flex items-center"
                              >
                                <span className="mr-2">{code.flag}</span>
                                <span className="flex-1 truncate">{code.label}</span>
                                <CheckIcon
                                  className={cn(
                                    "ml-auto h-4 w-4",
                                    currentCountry?.code === code.code
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <div className="relative flex-1">
                    <Input
                      id="phone-number"
                      type="tel"
                      placeholder="Enter phone number"
                      value={tempPhoneNumber}
                      onChange={handlePhoneNumberChange}
                      ref={phoneInputRef}
                      className={cn(
                        phoneNumberValid === false ? "border-red-500 pr-8" : "",
                        "flex-1"
                      )}
                    />
                    {phoneNumberValid === false && (
                      <AlertCircle className="h-4 w-4 text-red-500 absolute right-2 top-1/2 transform -translate-y-1/2" />
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Enter your phone number including any local area codes.
                </p>
              </div>
              
              {(!preferences.whatsapp.isVerified || isVerificationNeeded) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={verifyPhoneNumber}
                  disabled={isVerifying || isUnlinking || !phoneNumberValid || isVerificationInProgress}
                  className="mt-2"
                >
                  {(isVerifying || isUnlinking) ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {verifyButtonText()}
                    </>
                  ) : (
                    "Verify Phone Number"
                  )}
                </Button>
              )}
              
              {preferences.whatsapp.isVerified && !isVerificationNeeded && (
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-green-600">
                    <CheckIcon className="h-4 w-4 mr-1" />
                    <span>Phone number verified</span>
                  </div>
                  
                  {showWhatsAppPrompt && (
                    <div className="p-4 rounded-md bg-muted">
                      <p className="text-sm mb-3">
                        To complete WhatsApp notification setup, you need to initiate a chat with our WhatsApp number:
                      </p>
                      <Button 
                        className="w-full sm:w-auto"
                        onClick={() => window.open(getWhatsAppLink(), '_blank')}
                      >
                        <Phone className="mr-2 h-4 w-4" />
                        Open WhatsApp Chat
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            <Label htmlFor="daily-summary" className="font-medium">
              Daily Summary (WhatsApp)
            </Label>
          </div>
          <Switch
            id="daily-summary"
            checked={preferences.dailySummary.whatsappEnabled}
            disabled={isSaving}
            onCheckedChange={(value) => handleToggleChange('dailySummary', value)}
          />
        </div>
        {(!preferences.whatsapp.enabled || !preferences.whatsapp.isVerified) && (
          <p className="text-sm text-muted-foreground mt-1">
            Enable and verify WhatsApp to use daily summary.
          </p>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Bell className="h-4 w-4 text-primary" />
            <Label htmlFor="web-push" className="font-medium">
              Web Push Notifications
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            {permissionStatus === 'denied' && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  toast({
                    title: "Permission required",
                    description: "Please unblock notifications in your browser settings and refresh the page",
                  });
                }}
              >
                Unblock
              </Button>
            )}
            <Switch
              id="web-push"
              checked={preferences.webPush.enabled}
              disabled={isSaving || !notificationsSupported || permissionStatus === 'denied'}
              onCheckedChange={(value) => handleToggleChange('webPush', value)}
            />
          </div>
        </div>
        
        {!notificationsSupported && (
          <p className="text-sm text-amber-600 mt-1">
            Your browser doesn't support web push notifications.
          </p>
        )}
        
        {permissionStatus === 'denied' && (
          <p className="text-sm text-destructive mt-1">
            You've blocked notifications in your browser. Please update your browser settings to enable notifications.
          </p>
        )}
        
        {preferences.webPush.enabled && (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Registered Devices</h4>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => fetchRegisteredDevices(currentFcmToken || undefined)}
                disabled={isLoadingDevices}
              >
                {isLoadingDevices ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Smartphone className="h-4 w-4 mr-2" />
                )}
                Refresh Devices
              </Button>
            </div>
            
            {isLoadingDevices ? (
              <div className="space-y-2">
                <div className="h-16 bg-muted rounded animate-pulse"></div>
                <div className="h-16 bg-muted rounded animate-pulse"></div>
              </div>
            ) : registeredDevices.length === 0 && !isLoadingDevices ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">No devices registered for notifications.</p>
                
                <Button
                  variant="default"
                  size="sm"
                  onClick={registerCurrentDevice}
                  disabled={isAddingCurrentDevice}
                  className="w-full sm:w-auto"
                >
                  {isAddingCurrentDevice ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Smartphone className="mr-2 h-4 w-4" />
                  )}
                  Register Current Device
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {currentDeviceFound === false && (
                  <div className="mb-3">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={registerCurrentDevice}
                      disabled={isAddingCurrentDevice}
                      className="w-full sm:w-auto"
                    >
                      {isAddingCurrentDevice ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Smartphone className="mr-2 h-4 w-4" />
                      )}
                      Register Current Device
                    </Button>
                  </div>
                )}
                
                {registeredDevices.map((device) => {
                  const isCurrent = isCurrentDevice(device);
                   
                  return (
                    <div 
                      key={device.id} 
                      className={cn(
                        "p-3 rounded-md border flex flex-col",
                        isCurrent ? "border-primary bg-primary/5" : "border-border"
                      )}
                    >
                      <div className="flex justify-between items-center">
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <Smartphone className="h-4 w-4 mr-2 text-primary" />
                            <span className="font-medium text-sm">
                              {device.device}
                              {isCurrent && (
                                <span className="ml-2 text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                                  Current Device
                                </span>
                              )}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Last active: {formatTimestamp(device.lastUsedAt)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => device.id && removeDevice(device.id)}
                          disabled={isRemovingDevice === device.id}
                          className="h-8 w-8"
                        >
                          {isRemovingDevice === device.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash className="h-4 w-4" />
                          )}
                          <span className="sr-only">Remove device</span>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      
      <PhoneVerificationDialog
        isOpen={showVerificationDialog}
        onClose={() => {
          setShowVerificationDialog(false);
          setConfirmationResult(null);
          setIsVerificationInProgress(false);
          cleanupRecaptcha();
        }}
        phoneNumber={tempPhoneNumber}
        onVerify={handleVerifyCode}
        error={verificationError}
      />
    </div>
  );
}
